/* RESPONSIBLE TEAM: team-inbox */
import Service, { inject as service } from '@ember/service';
import type Router from '@ember/routing/router-service';
import type Session from './session';
import type InboxState from './inbox-state';
import { InboxEvents } from './inbox-state';
import { InboxType } from 'embercom/models/data/inbox/inbox-types';
import { type InboxIdentifier } from 'embercom/objects/inbox/inboxes/inbox';
import type InboxSidebarService from './inbox-sidebar-service';
import type CommandKService from './command-k';
import { DisplayContext, type ActionRegistration } from './command-k';
import type PermissionsModal from 'embercom/services/permissions-modal';
import type IntlService from 'embercom/services/intl';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import type TracingService from 'embercom/services/tracing';
import type CopilotApi from 'embercom/services/copilot-api';
import type ConversationUpdates from './conversation-updates';
import Conversation, { ConversationState } from 'embercom/objects/inbox/conversation';
import type TicketCustomState from 'embercom/objects/inbox/ticket-custom-state';
import type TicketStateService from 'embercom/services/ticket-state-service';
import { buildUpdateTicketStateCommandKContext } from 'embercom/lib/inbox2/ticket-custom-states';
import { RenderableType } from 'embercom/models/data/inbox/renderable-types';
import type WorkflowConnectorAction from 'embercom/objects/inbox/workflow-connector-action';
import type Macro from 'embercom/objects/inbox/macro';
import type SavedReplyInsertionsService from 'embercom/services/saved-reply-insertions-service';
import type { CustomEmoji } from 'embercom/services/custom-emoji-service';
import type { GifSearchEntry } from 'embercom/objects/inbox/command-k/gifs';
import { type BlockList } from '@intercom/interblocks.ts';
import { type DurationObject } from 'embercom/objects/inbox/duration';
import { taskFor } from 'ember-concurrency-ts';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';

/**
 * Handles bridge event subscriptions between the React conversation stream
 * and Ember services. Extracted from inbox-state.ts to keep bridge plumbing
 * separate from core inbox state logic.
 */
export default class InboxConversationStreamBridgeService extends Service {
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare session: Session;
  @service declare inboxState: InboxState;
  @service declare router: Router;
  @service declare inboxSidebarService: InboxSidebarService;
  @service declare commandK: CommandKService;
  @service declare permissionsModal: PermissionsModal;
  @service declare intl: IntlService;
  @service declare tracing: TracingService;
  @service declare copilotApi: CopilotApi;
  @service declare conversationUpdates: ConversationUpdates;
  @service declare savedReplyInsertionsService: SavedReplyInsertionsService;
  @service declare ticketStateService: TicketStateService;

  private unsubscribers: Array<() => void> = [];

  willDestroy() {
    super.willDestroy();
    for (let unsubscribe of this.unsubscribers) {
      unsubscribe();
    }
    this.unsubscribers = [];
  }

  setupSubscriptions() {
    if (this.unsubscribers.length > 0) {
      return;
    }

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'save-reply-as-macro',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          this.inboxState.createMacroBlocks = data['blocks'] as BlockList | undefined;
          this.inboxState.createMacroType = 1; // MacroType.Reply
          let hasPermission = this.inboxState.handleCreateMacroAction('react_message_actions');
          if (hasPermission) {
            this.inboxState.showCreateMacroModal = true;
          }
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'reply-to-email-part',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          let partEntityId = data['partEntityId'] as number;
          this.inboxState.replyToEmailPart({ entityId: partEntityId }, conversationId);
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'forward-message',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          let partEntityId = data['partEntityId'] as number;
          let entityType = data['entityType'] as number | undefined;
          let partType = entityType !== undefined && entityType !== 70 ? 'initial-part' : 'comment';
          let partId = `${partType}-${conversationId}-${partEntityId}`;
          this.router.transitionTo('inbox.workspace.inbox.new-conversation', {
            queryParams: { forwardedPartId: partId, ticketTypeId: undefined },
          });
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'sidebar:preview-conversation',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          this.inboxSidebarService.previewConversation({ id: conversationId } as Conversation);
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'sidebar:preview-side-conversation',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let sideConversationId = data['sideConversationId'] as number;
          let parentConversationId = data['parentConversationId'] as number;
          this.inboxSidebarService.previewSideConversation(
            sideConversationId,
            parentConversationId,
            true,
          );
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'sidebar:view-call-details',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let callData = data['call'] as Record<string, unknown>;
          this.inboxSidebarService.toggleIsViewingPhoneDetails(callData as any);
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent('sidebar:toggle', () => {
        if (!this.isStreamEnabled) {
          return;
        }
        this.inboxSidebarService.toggleSidebarVisibility();
      }),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent('trigger-workflow-connector', () => {
        if (!this.isStreamEnabled) {
          return;
        }
        if (!this.session.teammate.permissions.canUseDataConnectors) {
          this.permissionsModal.showModal({
            permissionsLabel: this.intl.t('inbox.common.permissions-label.can-use-data-connectors'),
          });
          return;
        }
        let conversation = this.inboxState.activeConversation;
        if (!conversation || !(conversation instanceof Conversation)) {
          return;
        }
        this.commandK.setActivationContext(DisplayContext.MessageActions);
        this.commandK.registerAndShow({
          actionID: 'trigger-workflow-connector-action-part',
          onSelect: (action: WorkflowConnectorAction) => {
            taskFor(this.inboxState.triggerWorkflowConnectorAction).perform(
              conversation,
              null,
              action,
            );
          },
          activatingComponent: 'conversation-part-message-actions',
        });
      }),
    );

    // React composer: open Command-K macro picker when toolbar button is clicked
    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'composer:use-macro',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          let pane = data['pane'] as string;

          this.commandK.setActivationContext(DisplayContext.MessageActions);
          this.commandK.registerAndShow({
            actionID: 'use-macro',
            context: { pane, isStartingConversation: false },
            onSelect: async (macro: Macro) => {
              let rendered = await macro.render(
                this.session.workspace.id,
                false,
                conversationId.toString(),
              );

              this.reactTeammateAppIsland.dispatchTeammateAppEvent('composer:macro-applied', {
                blocks: rendered.blocks,
                actions: rendered.actions.map((a) => ({
                  type: a.type,
                  actionData: a.data ?? {},
                })),
                macroId: macro.id,
                conversationId,
              });

              this.savedReplyInsertionsService.recordInsertion(
                this.session.workspace,
                this.session.teammate,
                macro,
                conversationId,
              );
            },
          });
        },
      ),
    );

    // React composer: open Command-K AI Assist when toolbar button is clicked.
    // Mirrors conversation-reply-composer.ts aiAssist() (L2478) and the
    // <Inbox2::CommandK::Action @id="ai-assist"> registration (L794-800).
    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'composer:use-ai-assist',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          let conversation = this.inboxState.activeConversation;

          this.commandK.setActivationContext(DisplayContext.MessageActions);
          this.commandK.registerAndShow({
            actionID: 'ai-assist',
            context: { conversation, composerIsEmpty: false },
            onSelect: async (promptKey: string) => {
              // add-summary creates a conversation part via Ember services
              // (no React composer involvement).
              if (promptKey === 'add-summary') {
                if (conversation instanceof Conversation) {
                  await this.inboxState.addSummaryToConversation(conversation);
                }
                return;
              }

              if (promptKey === 'ask-fin') {
                this.inboxSidebarService.openCopilot();
                return;
              }

              // All other actions (text transforms + insert-summary) are
              // signalled to React with minimal data. React owns the API
              // calls and editor mutations.
              this.reactTeammateAppIsland.dispatchTeammateAppEvent(
                'composer:ai-assist-action-selected',
                { conversationId, promptKey },
              );
            },
          });
        },
      ),
    );

    // React composer: open Command-K emoji picker when toolbar button is clicked
    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'composer:use-emoji',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;

          this.commandK.setActivationContext(DisplayContext.MessageActions);
          this.commandK.registerAndShow({
            actionID: 'emoji',
            onSelect: (emojiValue: string | CustomEmoji) => {
              if (typeof emojiValue !== 'string') {
                this.reactTeammateAppIsland.dispatchTeammateAppEvent('composer:emoji-selected', {
                  conversationId,
                  isCustomEmoji: true,
                  identifier: emojiValue.identifier,
                  src: emojiValue.src,
                  alt: emojiValue.alt,
                });
              } else {
                this.reactTeammateAppIsland.dispatchTeammateAppEvent('composer:emoji-selected', {
                  conversationId,
                  isCustomEmoji: false,
                  value: emojiValue,
                });
              }
            },
          });
        },
      ),
    );

    // React composer: open Command-K GIF picker when toolbar button is clicked
    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'composer:use-gif',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;

          this.commandK.setActivationContext(DisplayContext.MessageActions);
          this.commandK.registerAndShow({
            actionID: 'gifs',
            onSelect: (gif: GifSearchEntry) => {
              this.reactTeammateAppIsland.dispatchTeammateAppEvent('composer:gif-selected', {
                conversationId,
                url: gif.url,
                previewUrl: gif.preview_url,
                width: gif.width,
                height: gif.height,
                title: gif.title,
              });
            },
          });
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'open-copilot',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let query = data['query'] as string;
          if (query) {
            this.copilotApi.triggerAndSearchCopilotQuery(query);
          }
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'edit-note',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          let partEntityId = data['partEntityId'] as number;
          let blocks = data['blocks'] as BlockList | undefined;
          let part = { entityId: partEntityId, renderableData: { blocks: blocks ?? [] } };
          this.inboxState.editNote(part as unknown as RenderablePart, conversationId);
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'open-fin-answer-debugger',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          this.inboxState.finDebuggerPartEntityId = data['partEntityId'] as number;
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'conversation:mark-as-read',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          this.inboxState.trigger(InboxEvents.ConversationRead, { id: conversationId });
        },
      ),
    );

    // React→Ember: when React clears a mention, update Ember's local state.
    // The API call (mark_mentions_as_read) is expected to be made by the React
    // side before dispatching this event, so we only handle UI state here.
    // We intentionally avoid calling inboxState.markMentionsAsRead() to prevent
    // an infinite bridge event loop (it dispatches back to React).
    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'conversation:clear-mention',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          let mentionsInbox: InboxIdentifier = {
            type: InboxType.Mentions,
            id: InboxType.Mentions,
          };
          this.inboxState.inbox2Counters.decrementCount(mentionsInbox);
          this.inboxState.trigger(InboxEvents.ConversationRead, { id: conversationId });
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'conversation-stream:rendered',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let span = this.tracing.getActiveRootSpanByResource('inbox2:conversation_stream');
          if (span) {
            this.tracing.endChildSpan({
              span,
              attributes: {
                conversation_renderable_part_count: data['renderablePartCount'] as number,
                rendered_by: 'react',
                served_from_cache: data['servedFromCache'] as boolean,
              },
            });
          }
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'conversation-stream:retry',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversation = this.inboxState.activeConversation;
          if (!conversation || !(conversation instanceof Conversation)) {
            return;
          }

          let clientAssignedUuid = data['clientAssignedUuid'] as string;
          let failedPart = conversation.pendingParts?.find(
            (p) => p.clientAssignedUuid === clientAssignedUuid,
          );
          if (!failedPart) {
            return;
          }

          let existingUpdate = this.conversationUpdates.existingUpdate(failedPart, conversation);
          if (existingUpdate) {
            this.reactTeammateAppIsland.dispatchTeammateAppEvent(
              'conversation-stream:optimistic-fail',
              {
                conversationId: conversation.id,
                clientAssignedUuid,
                action: 'rollback',
              },
            );
            this.conversationUpdates.rollbackUpdates(conversation.id, [existingUpdate]);
          }

          let isNote = failedPart.renderableType === RenderableType.AdminNote;
          this.inboxState.retrySendReplyOrNote({
            isNote,
            conversation,
            part: failedPart,
          });
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'redact-message',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let partEntityId = data['partEntityId'] as number;
          this.inboxState.redactPartEntityId = partEntityId;
        },
      ),
    );

    // When the React composer mounts, register Cmd+K actions that would normally
    // be registered by the Ember composer template (conversation-reply-composer.hbs).
    // Without this, actions like upload-attachment, insert-article, compose-note etc.
    // are missing from the Cmd+K palette.
    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'composer:actions-mounted',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          let pane = data['pane'] as string;
          this.registerReactComposerCommandKActions(conversationId, pane);
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent('composer:actions-unmounted', () => {
        if (!this.isStreamEnabled) {
          return;
        }
        this.deregisterReactComposerCommandKActions();
      }),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'commandk:open',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let type = data['type'] as string;
          if (type === 'show') {
            this.commandK.show();
          } else if (type === 'snooze') {
            let conversation = this.inboxState.activeConversation;
            if (!conversation || !(conversation instanceof Conversation)) {
              return;
            }
            this.commandK.registerAndShow({
              actionID: 'snooze',
              onSelect: (duration: DurationObject) => {
                this.inboxState.snoozeConversation(conversation, duration);
              },
            });
          } else if (type === 'update-ticket-state') {
            let conversation = this.inboxState.activeConversation;
            if (
              !conversation ||
              !(conversation instanceof Conversation) ||
              !conversation.isTicket
            ) {
              return;
            }

            let customStates = conversation.ticketType
              ? this.ticketStateService.getLiveCustomStatesForTicketType(conversation.ticketType)
              : this.ticketStateService.customStates;

            let currentCustomState = this.ticketStateService.getTicketCustomStateById(
              conversation.ticketCustomStateId,
            );

            let context = buildUpdateTicketStateCommandKContext({
              customStates,
              selectedCustomStateId: currentCustomState?.id,
              allowClosedState: true,
              intl: this.intl,
              conversation,
            });

            this.commandK.registerAndShow({
              actionID: 'update-ticket-state',
              context,
              onSelect: async (state: ConversationState | TicketCustomState['id']) => {
                if (state === ConversationState.Closed) {
                  this.inboxState.trigger(InboxEvents.CloseConversationRequested);
                  return;
                }
                let ticketCustomState = this.ticketStateService.getTicketCustomStateById(
                  state as TicketCustomState['id'],
                );
                await this.inboxState.changeTicketStateLocalUpdates(
                  conversation,
                  ticketCustomState,
                );
              },
            });
          }
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent('conversation:close', () => {
        if (!this.isStreamEnabled) {
          return;
        }
        this.inboxState.trigger(InboxEvents.CloseConversationRequested);
      }),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent('conversation:reopen', () => {
        if (!this.isStreamEnabled) {
          return;
        }
        this.inboxState.trigger(InboxEvents.OpenConversationRequested);
      }),
    );
  }

  // Cmd+K action registrations created by the React composer bridge.
  // Tracked so we can deactivate them when the React composer unmounts.
  private reactComposerActionRegistrations: ActionRegistration[] = [];

  private registerReactComposerCommandKActions(conversationId: number, pane: string) {
    // Clean up any previous registrations first
    this.deregisterReactComposerCommandKActions();

    let dispatch = (event: string, extra?: Record<string, unknown>) => {
      this.reactTeammateAppIsland.dispatchTeammateAppEvent(event, {
        conversationId,
        ...extra,
      });
    };

    let actions: ActionRegistration[] = [
      // Actions that trigger React-native behavior (file pickers, inserters)
      {
        actionID: 'upload-attachment',
        onSelect: () => dispatch('composer:command-k-action', { action: 'upload-attachment' }),
      },
      {
        actionID: 'upload-image',
        onSelect: () => dispatch('composer:command-k-action', { action: 'upload-image' }),
      },
      {
        actionID: 'insert-article',
        onSelect: () => dispatch('composer:command-k-action', { action: 'insert-article' }),
      },
      // Pane switching
      {
        actionID: 'compose-note',
        onSelect: () => dispatch('composer:set-pane', { pane: 'note' }),
      },
      {
        actionID: 'compose-reply',
        onSelect: () => dispatch('composer:set-pane', { pane: 'reply' }),
      },
    ];

    // Only show compose-note when reply pane is active and vice versa
    actions = actions.filter((a) => {
      if (a.actionID === 'compose-note' && pane === 'note') {
        return false;
      }
      if (a.actionID === 'compose-reply' && pane === 'reply') {
        return false;
      }
      return true;
    });

    for (let action of actions) {
      this.commandK.activate(action);
    }
    this.reactComposerActionRegistrations = actions;
  }

  private deregisterReactComposerCommandKActions() {
    for (let action of this.reactComposerActionRegistrations) {
      this.commandK.deactivate(action);
    }
    this.reactComposerActionRegistrations = [];
  }

  private get isStreamEnabled(): boolean {
    return this.session.workspace.canUseReactInboxConversationStream;
  }
}

declare module '@ember/service' {
  interface Registry {
    inboxConversationStreamBridge: InboxConversationStreamBridgeService;
    'inbox-conversation-stream-bridge': InboxConversationStreamBridgeService;
  }
}
